import { MIN_PATIENT_AGE, REQUIRED_CONSENT_KEYS } from '@/constants/intakeForm';

// Parse a date string in either the canonical "MM/DD/YYYY" form or the legacy
// "YYYY-MM-DD" form. Returns { y, m, d } numbers or null if the string is
// malformed / contains placeholder underscores (incomplete entry).
const parseDob = (str) => {
  if (typeof str !== 'string') return null;
  if (str.includes('_')) return null;
  const slashMatch = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (slashMatch) {
    return {
      m: parseInt(slashMatch[1], 10),
      d: parseInt(slashMatch[2], 10),
      y: parseInt(slashMatch[3], 10),
    };
  }
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return {
      y: parseInt(isoMatch[1], 10),
      m: parseInt(isoMatch[2], 10),
      d: parseInt(isoMatch[3], 10),
    };
  }
  return null;
};

export const isValidDob = (str) => {
  const parsed = parseDob(str);
  if (!parsed) return false;
  const { y, m, d } = parsed;
  if (y < 1900) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  const dob = new Date(Date.UTC(y, m - 1, d));
  if (
    dob.getUTCFullYear() !== y ||
    dob.getUTCMonth() + 1 !== m ||
    dob.getUTCDate() !== d
  ) {
    return false;
  }
  const now = new Date();
  if (dob.getTime() >= now.getTime()) return false;
  const age = (now - dob) / (1000 * 60 * 60 * 24 * 365.25);
  if (age < MIN_PATIENT_AGE) return false;
  return true;
};

export const validatePatientInfo = (r) => {
  const errors = {};
  if (!isValidDob(r.date_of_birth)) {
    errors.date_of_birth =
      'Enter a real date of birth. You must be at least 18.';
  }
  if (!r.address || !r.address.trim()) errors.address = 'Address is required.';
  if (!r.city || !r.city.trim()) errors.city = 'City is required.';
  if (!r.zip || !/^\d{5}$/.test(r.zip)) {
    errors.zip = 'ZIP code must be 5 digits.';
  }
  const ft = parseInt(r.height_ft, 10);
  const inch = parseInt(r.height_in, 10);
  if (isNaN(ft) || ft < 3 || ft > 7 || isNaN(inch) || inch < 0 || inch > 11) {
    errors.height = 'Enter your height.';
  }
  const weight = parseInt(r.weight_lbs, 10);
  if (isNaN(weight) || weight < 50 || weight > 700) {
    errors.weight_lbs = 'Enter your weight in pounds (between 50 and 700).';
  }
  return errors;
};

// Returns an array of human-readable strings listing every still-missing item
// for the final submit gate. Each entry has { label, pageIndex, fieldId } so
// the orchestrator can scroll-and-focus when the patient taps a missing item.
export const buildSubmitChecklist = ({
  responses,
  initials,
  consents,
  signedName,
  attested,
}) => {
  const missing = [];

  const piErrors = validatePatientInfo(responses);
  if (piErrors.date_of_birth)
    missing.push({
      label: 'Date of birth',
      pageIndex: 0,
      fieldId: 'dob_mm',
    });
  if (piErrors.address)
    missing.push({
      label: 'Street address',
      pageIndex: 0,
      fieldId: 'address',
    });
  if (piErrors.city)
    missing.push({ label: 'City', pageIndex: 0, fieldId: 'city' });
  if (piErrors.zip)
    missing.push({ label: 'ZIP code', pageIndex: 0, fieldId: 'zip' });
  if (piErrors.height)
    missing.push({ label: 'Height', pageIndex: 0, fieldId: 'height_ft' });
  if (piErrors.weight_lbs)
    missing.push({ label: 'Weight', pageIndex: 0, fieldId: 'weight_lbs' });

  if (!initials || initials.trim().length < 2) {
    missing.push({
      label: 'Your initials on the consent page',
      pageIndex: 5,
      fieldId: 'consent_initials',
    });
  }

  REQUIRED_CONSENT_KEYS.forEach((key, idx) => {
    if (!consents[key]) {
      missing.push({
        label: `Agreement to consent ${idx + 1}`,
        pageIndex: 5,
        fieldId: `consent_${key}`,
      });
    }
  });

  if (!signedName || signedName.trim().length < 2) {
    missing.push({
      label: 'Typed signature (your full legal name)',
      pageIndex: 5,
      fieldId: 'signed_name_input',
    });
  }

  if (!attested) {
    missing.push({
      label: 'Confirmation that the typed name is your signature',
      pageIndex: 5,
      fieldId: 'signed_attestation_checkbox',
    });
  }

  return missing;
};
